import {
    Box,
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    OutlinedInput,
    TextField,
} from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from '@material-ui/core/styles'
import BackgroundImage from '../../../../assets/login_background.png'
import EzosLogo from '../../../../assets/ezos-logo-primary-dark.png'
import { useHistory, useLocation } from 'react-router-dom'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { useForm } from 'react-hook-form'
import { useAuth, useUser as useClerkUser, useSignIn } from '@clerk/clerk-react'
import { useUser } from '../../../../hooks'
import LoopIcon from '@material-ui/icons/Loop'
import { Container } from '../../../../components'
import { Routes } from '../../../../navigation/config/routes'
import { typographyStyle } from '../../../../theme/typographyStyle'
import {
    LocalStorage,
    axiosInstance,
    resolvePostLoginRoute,
    storeLoginNext,
} from '../../../../helpers'

const AUTH_CALLBACK_URL =
    typeof window !== 'undefined'
        ? `${window.location.origin}/auth-callback`
        : '/auth-callback'
const CLERK_LOGIN_PATH =
    process.env.REACT_APP_CLERK_LOGIN_PATH || 'user/clerk_login/'
const GET_TOKEN_RETRY_MS = 200
const GET_TOKEN_MAX_ATTEMPTS = 80

const GoogleLogoIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        style={{ flexShrink: 0 }}
    >
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
)

const MicrosoftLogoIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 23 23"
        style={{ flexShrink: 0 }}
    >
        <path fill="#F25022" d="M1 1h10v10H1z" />
        <path fill="#00A4EF" d="M12 1h10v10H12z" />
        <path fill="#7FBA00" d="M1 12h10v10H1z" />
        <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
)

interface State {
    password: string
    showPassword: boolean
    email: string
}

export const Login = () => {
    const theme = useTheme()
    const { signOut: clerkSignOut, getToken } = useAuth()
    const signInResult = useSignIn()
    const { isSignedIn } = useClerkUser()
    const isLoaded = signInResult?.isLoaded ?? false
    const signIn = signInResult?.signIn ?? null
    const { actions } = useUser()
    const { login } = actions
    const completedClerkLoginRef = useRef(false)

    const isLogout =
        typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).get('logout') === '1'
    // If we land on /login with Clerk signed in but no app token (e.g. fallback URL), run clerk_login here. Skip when user just logged out (?logout=1).
    useEffect(() => {
        if (
            isLogout ||
            !getToken ||
            !isSignedIn ||
            localStorage.getItem(LocalStorage.EZTURN4_TOKEN) ||
            completedClerkLoginRef.current
        )
            return
        completedClerkLoginRef.current = true
        const run = async () => {
            let clerkToken: string | null = null
            for (let i = 0; i < GET_TOKEN_MAX_ATTEMPTS; i++) {
                try {
                    clerkToken = await getToken()
                    if (clerkToken) break
                } catch {
                    /**/
                }
                await new Promise((r) => setTimeout(r, GET_TOKEN_RETRY_MS))
            }
            if (!clerkToken) {
                completedClerkLoginRef.current = false
                return
            }
            try {
                const { data: rootUser } = await axiosInstance.post(
                    CLERK_LOGIN_PATH,
                    { token: clerkToken, device: 'web' },
                    { headers: { 'Content-Type': 'application/json' } },
                )
                const workspace = rootUser?.workspaces?.[0]
                const token = workspace?.token ?? rootUser?.token
                if (token && workspace) {
                    localStorage.setItem(LocalStorage.EZTURN4_TOKEN, token)
                    axiosInstance.defaults.headers.common[
                        'Authorization'
                    ] = `token ${token}`
                    window.location.href = resolvePostLoginRoute(
                        workspace.active_workspace?.company_type,
                    )
                } else {
                    if (clerkSignOut) await clerkSignOut()
                    window.location.href = `${
                        typeof window !== 'undefined'
                            ? window.location.origin
                            : ''
                    }/login?error=invalid_email`
                }
            } catch {
                completedClerkLoginRef.current = false
                if (clerkSignOut) await clerkSignOut()
                window.location.href = `${
                    typeof window !== 'undefined' ? window.location.origin : ''
                }/login?error=invalid_email`
            }
        }
        run()
    }, [getToken, isSignedIn, isLogout])

    // Only sign out of Clerk when user explicitly logged out (?logout=1). Do not sign out when they're just on login
    // without a session (e.g. about to click Google) — that can cancel the OAuth redirect and block account selection.
    useEffect(() => {
        if (!isLoaded || !isLogout) return
        if (localStorage.getItem(LocalStorage.EZTURN4_TOKEN)) return
        if (clerkSignOut) clerkSignOut()
    }, [isLoaded, isLogout, clerkSignOut])

    const loginOrigin =
        typeof window !== 'undefined' ? window.location.origin : ''
    async function tryClerkLoginAndRedirect(): Promise<boolean> {
        if (!getToken) return false
        let clerkToken: string | null = null
        for (let i = 0; i < GET_TOKEN_MAX_ATTEMPTS; i++) {
            try {
                clerkToken = await getToken()
                if (clerkToken) break
            } catch {
                /**/
            }
            await new Promise((r) => setTimeout(r, GET_TOKEN_RETRY_MS))
        }
        if (!clerkToken) return false
        try {
            const { data: rootUser } = await axiosInstance.post(
                CLERK_LOGIN_PATH,
                { token: clerkToken, device: 'web' },
                { headers: { 'Content-Type': 'application/json' } },
            )
            const workspace = rootUser?.workspaces?.[0]
            const token = workspace?.token ?? rootUser?.token
            if (token && workspace) {
                localStorage.setItem(LocalStorage.EZTURN4_TOKEN, token)
                axiosInstance.defaults.headers.common[
                    'Authorization'
                ] = `token ${token}`
                window.location.href = resolvePostLoginRoute(
                    workspace.active_workspace?.company_type,
                )
                return true
            }
        } catch {
            /**/
        }
        return false
    }

    const [values, setValues] = React.useState<State>({
        password: '',
        showPassword: false,
        email: '',
    })
    const [isTouched, setIsTouched] = useState(false)
    const [loginError, setLoginError] = useState<null | string>(null)
    const [isLoading, setIsLoading] = useState(false)
    const location = useLocation()

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        storeLoginNext(params.get('next'))
    }, [location.search])

    // Show "email not valid" when redirected from auth-callback after clerk_login failed (e.g. SSO email not in system)
    useEffect(() => {
        const params = new URLSearchParams(location.search)
        if (params.get('error') === 'invalid_email') {
            setLoginError('This email is not valid for this application.')
            params.delete('error')
            const newSearch = params.toString()
            if (window.history.replaceState) {
                window.history.replaceState(
                    null,
                    '',
                    `${location.pathname}${newSearch ? `?${newSearch}` : ''}`,
                )
            }
        }
    }, [location.search, location.pathname])

    const handleChange = (
        prop: keyof State,
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (event.target.value !== '' && !isTouched && loginError) {
            setIsTouched(true)
        }
        setValues({ ...values, [prop]: event.target.value })
    }

    if (isTouched && loginError) {
        setLoginError(null)
    }

    const handleClickShowPassword = () => {
        setValues({ ...values, showPassword: !values.showPassword })
    }

    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        event.preventDefault()
    }

    const history = useHistory()

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'all',
    })

    const onSubmit = (data: any, event: any) => {
        setIsTouched(false)
        event.preventDefault()
        setIsLoading(true)
        login(data)
            .then((response) => {
                setLoginError(null)
                setIsLoading(false)
                try {
                    const rootUser = response?.data
                    const workspace = rootUser?.workspaces?.[0]
                    history.push(
                        resolvePostLoginRoute(
                            workspace?.active_workspace?.company_type,
                        ),
                    )
                } catch {
                    history.push(resolvePostLoginRoute())
                }
            })
            .catch((e) => {
                let errorMsg = 'Unable to login'
                try {
                    errorMsg =
                        e.response?.data?.non_field_errors?.[0] ?? errorMsg
                } catch {
                    // keep default
                }
                setLoginError(errorMsg)
                setIsLoading(false)
            })
    }

    return (
        <Container
            style={{
                height: '100vh',
                width: '100vw',
                backgroundImage: `url(${BackgroundImage})`,
                backgroundSize: 'cover',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
            }}
        >
            <Container
                style={{
                    backgroundColor: 'white',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: '0 0 20px rgba(0, 0, 0, 0.2)',
                    height: 600,
                    maxWidth: 600,
                    borderRadius: '40px',
                    flexDirection: 'column',
                }}
            >
                <Container
                    style={{
                        alignItems: 'center',
                        flexDirection: 'column',
                        margin: theme.spacing(2),
                    }}
                >
                    <img
                        src={EzosLogo}
                        style={{
                            padding: theme.spacing(1),
                            height: 61,
                        }}
                    />
                    <span
                        style={{
                            ...(typographyStyle(
                                theme.typography.h4,
                            ) as React.CSSProperties),
                            fontWeight: 700,
                            color: theme.palette.grey[700],
                        }}
                    >
                        EZOS Login
                    </span>
                </Container>
                <Container
                    style={{
                        flexDirection: 'column',
                        margin: theme.spacing(2),
                    }}
                >
                    {/* Always reserve space for SSO buttons so they don't pop in when Clerk loads */}
                    <Box
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: theme.spacing(1.5),
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        {isLoaded && signIn ? (
                            <>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    fullWidth
                                    style={{
                                        height: 49,
                                        textTransform: 'none',
                                    }}
                                    startIcon={<GoogleLogoIcon />}
                                    onClick={async () => {
                                        try {
                                            await signIn!.authenticateWithRedirect(
                                                {
                                                    strategy: 'oauth_google',
                                                    redirectUrl: AUTH_CALLBACK_URL,
                                                    redirectUrlComplete: AUTH_CALLBACK_URL,
                                                },
                                            )
                                        } catch {
                                            if (
                                                !(await tryClerkLoginAndRedirect())
                                            ) {
                                                await clerkSignOut?.()
                                                window.location.href = `${loginOrigin}/login`
                                            }
                                        }
                                    }}
                                >
                                    Sign in with Google
                                </Button>
                                <Button
                                    type="button"
                                    variant="outlined"
                                    fullWidth
                                    style={{
                                        height: 49,
                                        textTransform: 'none',
                                    }}
                                    startIcon={<MicrosoftLogoIcon />}
                                    onClick={async () => {
                                        try {
                                            await signIn!.authenticateWithRedirect(
                                                {
                                                    strategy: 'oauth_microsoft',
                                                    redirectUrl: AUTH_CALLBACK_URL,
                                                    redirectUrlComplete: AUTH_CALLBACK_URL,
                                                },
                                            )
                                        } catch {
                                            if (
                                                !(await tryClerkLoginAndRedirect())
                                            ) {
                                                await clerkSignOut?.()
                                                window.location.href = `${loginOrigin}/login`
                                            }
                                        }
                                    }}
                                >
                                    Sign in with Microsoft
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disabled
                                    style={{
                                        height: 49,
                                        textTransform: 'none',
                                    }}
                                    startIcon={<GoogleLogoIcon />}
                                >
                                    Sign in with Google
                                </Button>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    disabled
                                    style={{
                                        height: 49,
                                        textTransform: 'none',
                                    }}
                                    startIcon={<MicrosoftLogoIcon />}
                                >
                                    Sign in with Microsoft
                                </Button>
                            </>
                        )}
                    </Box>
                    <Box
                        style={{
                            marginBottom: theme.spacing(2),
                            color: theme.palette.grey[600],
                            fontSize: 14,
                            textAlign: 'center',
                        }}
                    >
                        or sign in with email
                    </Box>
                    <form
                        noValidate
                        autoComplete="off"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <FormControl fullWidth style={{ marginBottom: '20px' }}>
                            <TextField
                                id="email"
                                placeholder="Email"
                                variant="outlined"
                                type="email"
                                {...register('email', {
                                    required: true,
                                    pattern: /\S+@\S+\.\S+/,
                                    onChange: (
                                        event: React.ChangeEvent<HTMLInputElement>,
                                    ) => handleChange('email', event),
                                })}
                                error={
                                    errors?.email?.type === 'required' ||
                                    errors?.email?.type === 'pattern' ||
                                    loginError !== null
                                }
                            />
                            <Box>
                                {errors.email &&
                                    errors.email.type === 'required' && (
                                        <span className="error-message">
                                            This is required field
                                        </span>
                                    )}
                                {errors.email &&
                                    errors.email.type === 'pattern' && (
                                        <span className="error-message">
                                            Enter a valid email
                                        </span>
                                    )}
                            </Box>
                        </FormControl>

                        <FormControl fullWidth style={{ marginBottom: '20px' }}>
                            <OutlinedInput
                                id="standard-adornment-password"
                                type={values.showPassword ? 'text' : 'password'}
                                placeholder="Password"
                                value={values.password}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={
                                                handleMouseDownPassword
                                            }
                                        >
                                            {values.showPassword ? (
                                                <Visibility />
                                            ) : (
                                                <VisibilityOff />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                {...register('password', {
                                    required: true,
                                    onChange: (
                                        event: React.ChangeEvent<HTMLInputElement>,
                                    ) => handleChange('password', event),
                                })}
                                error={
                                    errors?.password?.type === 'required' ||
                                    loginError !== null
                                }
                            />
                            <Box>
                                {errors.password &&
                                    errors.password.type === 'required' && (
                                        <span className="error-message">
                                            This is required field
                                        </span>
                                    )}
                            </Box>
                            {loginError ? (
                                <span className="error-message">
                                    {loginError}
                                </span>
                            ) : null}
                        </FormControl>

                        <Button
                            variant="contained"
                            style={{
                                background: theme.palette.primary.dark,
                                color: '#FFF',
                                textTransform: 'inherit',
                                fontWeight: 500,
                                borderRadius: theme.shape.borderRadius,
                                boxShadow: 'none',
                                width: '100%',
                                marginTop: theme.spacing(1),
                                height: '49px',
                                transition:
                                    'background-color 0.3s, color 0.3s, border-color 0.3s',
                                border: `2px solid ${theme.palette.primary.dark}`,
                            }}
                            type="submit"
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'white'
                                e.currentTarget.style.color =
                                    theme.palette.primary.dark
                                e.currentTarget.style.borderColor =
                                    theme.palette.primary.dark
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                    theme.palette.primary.dark
                                e.currentTarget.style.color = 'white'
                            }}
                        >
                            {isLoading ? (
                                <LoopIcon
                                    style={{
                                        animation: 'spin 01s linear infinite',
                                    }}
                                />
                            ) : (
                                'Sign in'
                            )}
                        </Button>
                    </form>

                    <Button
                        variant="contained"
                        style={{
                            background: theme.palette.primary.dark,
                            color: '#FFF',
                            textTransform: 'inherit',
                            fontWeight: 500,
                            borderRadius: theme.shape.borderRadius,
                            boxShadow: 'none',
                            width: '100%',
                            height: '49px',
                            marginTop: theme.spacing(2),
                            transition:
                                'background-color 0.3s, color 0.3s, border-color 0.3s',
                            border: `2px solid ${theme.palette.primary.dark}`,
                        }}
                        onClick={() => {
                            history.push(Routes.public.requestPasswordReset)
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'white'
                            e.currentTarget.style.color =
                                theme.palette.primary.dark
                            e.currentTarget.style.borderColor =
                                theme.palette.primary.dark
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                                theme.palette.primary.dark
                            e.currentTarget.style.color = 'white'
                        }}
                    >
                        Forgot Password?
                    </Button>
                </Container>
            </Container>
            <Container>
                <Button
                    style={{
                        color: 'white',
                        textTransform: 'none',
                        textShadow: '0 0 5px rgba(0, 0, 0, 1.0)',
                        fontWeight: 700,
                        position: 'relative',
                        backgroundColor: 'transparent',
                        ...(typographyStyle(
                            theme.typography.h6,
                        ) as React.CSSProperties),
                    }}
                    onClick={() => {
                        history.push(Routes.public.register)
                    }}
                    onMouseEnter={() => {
                        const line = document.querySelector<HTMLSpanElement>(
                            '#line',
                        )
                        if (line) {
                            line.style.width = '90%'
                        }
                    }}
                    onMouseLeave={() => {
                        const line = document.querySelector<HTMLSpanElement>(
                            '#line',
                        )
                        if (line) {
                            line.style.width = '0'
                        }
                    }}
                >
                    Don&apos;t have an account?
                    <span
                        id="line"
                        style={{
                            position: 'absolute',
                            bottom: 0,
                            width: 0,
                            height: '2px',
                            backgroundColor: 'white',
                            transition: 'width 0.3s ease',
                        }}
                    />
                </Button>
            </Container>
        </Container>
    )
}
